/**
 * Activity Log List Component
 * Displays activity logs in a timeline/table format
 */

import React, {useContext, useEffect, useState} from 'react';
import {Card, CardBody, Col, Row, Table} from 'reactstrap';
import Loader from '../../../../Layout/Loader';
import {H6, P} from '../../../../AbstractElements';
import ActivityLogService from '../Services/ActivityLog.service';
import AuthorizationContext from '../../../../_helper/Authorization';
import type {AuthorizationContextType} from '../../../../_helper/Authorization/AuthorizationTypes';
import type {ActivityLog, ActivityLogListProps, ActivityLogPaginationMeta} from '../Types/ActivityLogTypes';

const ActivityLogList: React.FC<ActivityLogListProps> = ({
                                                            subjectType,
                                                            subjectId,
                                                            pageSize = 10,
                                                            logNameFilter,
                                                            onError,
                                                            className = '',
                                                        }) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [pagination, setPagination] = useState<ActivityLogPaginationMeta | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    useEffect(() => {
        fetchLogs(currentPage);
    }, [subjectType, subjectId, logNameFilter]);

    const fetchLogs = (page: number) => {
        setLoaderVisibility(true);
        ActivityLogService.fetchActivityLogs({
            subject_type: subjectType,
            subject_id: subjectId,
            page: page,
            limit: pageSize,
            sort: 'desc',
            log_name: logNameFilter,
        })
            .then(response => {
                if (response?.data) {
                    setLogs(response.data);
                    if (response.meta) {
                        setPagination(response.meta);
                    }
                }
                setLoaderVisibility(false);
            })
            .catch(exception => {
                errorHandler(exception);
                setLoaderVisibility(false);
                if (onError) {
                    onError(exception);
                }
            });
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchLogs(page);
    };

    const toggleExpandedLog = (logId: number) => {
        setExpandedLogId(expandedLogId === logId ? null : logId);
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    const getCauserDisplayName = (log: ActivityLog): string => {
        if (log.causer?.name) {
            return log.causer.email ? `${log.causer.name} (${log.causer.email})` : log.causer.name;
        }
        return 'System';
    };

    if (logs.length === 0 && !loaderVisibility) {
        return (
            <Card className={className}>
                <CardBody className="text-center p-4">
                    <P className="text-muted">No activity logs found</P>
                </CardBody>
            </Card>
        );
    }

    return (
        <div className={className}>
            <Loader visibility={loaderVisibility}/>
            <Card>
                <CardBody>
                    <div className="activity-log-container">
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Action</th>
                                    <th>Details</th>
                                    <th>Date/Time</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => (
                                    <React.Fragment key={log.id}>
                                        <tr>
                                            <td>
                                                <span className="font-weight-600">
                                                    {getCauserDisplayName(log)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="badge badge-light-primary">
                                                    {log.log_name}
                                                </span>
                                            </td>
                                            <td className="text-truncate" style={{maxWidth: '300px'}}>
                                                {log.description}
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {formatDate(log.created_at)}
                                                </small>
                                            </td>
                                            <td>
                                                {log.properties && Object.keys(log.properties).length > 0 && (
                                                    <button
                                                        className="btn btn-sm btn-light"
                                                        onClick={() => toggleExpandedLog(log.id)}
                                                    >
                                                        {expandedLogId === log.id ? 'Hide' : 'Details'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {expandedLogId === log.id && log.properties && (
                                            <tr className="bg-light">
                                                <td colSpan={5}>
                                                    <div className="p-3">
                                                        <H6>Changes Made:</H6>
                                                        <div className="properties-display">
                                                            {Object.entries(log.properties).map(
                                                                ([key, value], idx) => (
                                                                    <Row key={idx} className="mb-2">
                                                                        <Col sm="3" className="font-weight-600">
                                                                            {key
                                                                                .split('_')
                                                                                .map(
                                                                                    (word) =>
                                                                                        word.charAt(0).toUpperCase() +
                                                                                        word.slice(1),
                                                                                )
                                                                                .join(' ')}
                                                                        </Col>
                                                                        <Col sm="9">
                                                                            {typeof value === 'object' ? (
                                                                                <pre className="bg-white p-2 rounded text-muted">
                                                                                    {JSON.stringify(value, null, 2)}
                                                                                </pre>
                                                                            ) : (
                                                                                <span>{String(value)}</span>
                                                                            )}
                                                                        </Col>
                                                                    </Row>
                                                                ),
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>

                        {pagination && pagination.last_page > 1 && (
                            <div className="d-flex justify-content-center mt-4">
                                <nav aria-label="Page navigation">
                                    <ul className="pagination">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(1)}
                                                disabled={currentPage === 1}
                                            >
                                                First
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </button>
                                        </li>

                                        {Array.from({length: Math.min(5, pagination.last_page)}, (_, i) => {
                                            const pageNum =
                                                currentPage <= 3
                                                    ? i + 1
                                                    : currentPage >= pagination.last_page - 2
                                                      ? pagination.last_page - 4 + i
                                                      : currentPage - 2 + i;
                                            return pageNum > 0 && pageNum <= pagination.last_page ? (
                                                <li
                                                    key={pageNum}
                                                    className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            ) : null;
                                        })}

                                        <li
                                            className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === pagination.last_page}
                                            >
                                                Next
                                            </button>
                                        </li>
                                        <li
                                            className={`page-item ${currentPage === pagination.last_page ? 'disabled' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(pagination.last_page)}
                                                disabled={currentPage === pagination.last_page}
                                            >
                                                Last
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default ActivityLogList;


